import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignHorizontalSpaceBetween,
  AlignStartHorizontal,
  AlignStartVertical,
  AlignVerticalSpaceBetween,
  FlipHorizontal2,
  FlipVertical2
} from "lucide-react";
import React from "react";
import { Shape } from "../../../../../types";
import { TooltipProvider } from "../../../../ui/tooltip";
import { useMapCanvas } from "../../../contexts/MapCanvasContext";
import { useMapEditorActions } from "../../../contexts/MapEditorContext";
import { getWindowKonvaObject } from "../../../helpers/konva/getWindowKonvaObject";
import {
  type ShapeMetrics,
  useShapeMetrics
} from "../../../helpers/shapeMetrics";
import { useEditorShortcuts } from "../../shortcuts/EditorShortcutContext/useEditorShortcuts";
import { ShapeControlButton } from "./controls/ShapeControlButton";
import { ShapeControlDelete } from "./controls/ShapeControlDelete";
import { ShapeControlHide } from "./controls/ShapeControlHide";
import { ShapeControlLock } from "./controls/ShapeControlLock";

interface MultiSelectionToolboxProps {
  selectedIds: string[];
}

/**
 * Compute the visual bounding box of shapes in canvas coordinates,
 * accounting for scaleX/scaleY flip.
 */
const getGroupBounds = (shapes: Shape[]) => {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const s of shapes) {
    const flippedX = (s.scaleX || 1) < 0;
    const flippedY = (s.scaleY || 1) < 0;
    const x0 = flippedX ? s.x - s.width : s.x;
    const x1 = flippedX ? s.x : s.x + s.width;
    const y0 = flippedY ? s.y - s.height : s.y;
    const y1 = flippedY ? s.y : s.y + s.height;
    minX = Math.min(minX, x0);
    maxX = Math.max(maxX, x1);
    minY = Math.min(minY, y0);
    maxY = Math.max(maxY, y1);
  }

  return { minX, maxX, minY, maxY };
};

/**
 * Compute screen-space bounding box for all selected shapes
 * by querying Konva client rects.
 */
const getSelectionScreenBounds = (
  selectedIds: string[],
  metrics: ShapeMetrics | null
) => {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  if (metrics) {
    for (const id of selectedIds) {
      const rect = metrics.getScreenRect(id);
      if (!rect) continue;
      minX = Math.min(minX, rect.left);
      maxX = Math.max(maxX, rect.right);
      minY = Math.min(minY, rect.top);
      maxY = Math.max(maxY, rect.bottom);
    }
    if (!Number.isFinite(minX)) return null;
    return { x: (minX + maxX) / 2, y: minY - 80 };
  }

  const stage = getWindowKonvaObject().stages[0];
  if (!stage) return null;

  const stageBox = stage.container().getBoundingClientRect();

  for (const id of selectedIds) {
    const node = stage.findOne(`#shape-${id}`);
    if (!node) continue;
    const box = node.getClientRect();
    minX = Math.min(minX, stageBox.left + box.x);
    maxX = Math.max(maxX, stageBox.left + box.x + box.width);
    minY = Math.min(minY, stageBox.top + box.y);
    maxY = Math.max(maxY, stageBox.top + box.y + box.height);
  }

  if (!Number.isFinite(minX)) return null;
  return { x: (minX + maxX) / 2, y: minY - 80 };
};

export const MultiSelectionToolbox: React.FC<MultiSelectionToolboxProps> = ({
  selectedIds
}) => {
  const { updateShape, deleteShape, alignSelection, distributeSelection } =
    useMapEditorActions();
  const { shapesRef } = useMapCanvas();
  const { shortcuts } = useEditorShortcuts();
  const metrics = useShapeMetrics();

  const shapes = React.useMemo(
    () =>
      selectedIds
        .map((id) => shapesRef.current.find((s) => s.id === id))
        .filter(Boolean) as Shape[],
    [selectedIds, shapesRef]
  );

  if (shapes.length < 2) return null;

  // Use the first shape for toggle state reference
  const firstShape = shapes[0];
  const hasNonSpot = shapes.some((s) => s.type !== "spot");

  const handleUpdate = (newProps: Partial<Shape>) => {
    selectedIds.forEach((id) => {
      updateShape(id, newProps);
    });
  };

  const handleFlipHorizontal = () => {
    const allShapes = selectedIds
      .map((id) => shapesRef.current.find((s) => s.id === id))
      .filter(Boolean) as Shape[];
    if (allShapes.length === 0) return;

    const { minX, maxX } = getGroupBounds(allShapes);

    for (const s of allShapes) {
      if (s.type === "spot") {
        // Mirror position only — spots don't flip visually
        updateShape(s.id, { x: minX + maxX - s.x - s.width });
      } else {
        const newScaleX = (s.scaleX || 1) * -1;
        updateShape(s.id, { scaleX: newScaleX, x: minX + maxX - s.x });
      }
    }
  };

  const handleFlipVertical = () => {
    const allShapes = selectedIds
      .map((id) => shapesRef.current.find((s) => s.id === id))
      .filter(Boolean) as Shape[];
    if (allShapes.length === 0) return;

    const { minY, maxY } = getGroupBounds(allShapes);

    for (const s of allShapes) {
      if (s.type === "spot") {
        updateShape(s.id, { y: minY + maxY - s.y - s.height });
      } else {
        const newScaleY = (s.scaleY || 1) * -1;
        updateShape(s.id, { scaleY: newScaleY, y: minY + maxY - s.y });
      }
    }
  };

  // Position above the selection bounding box
  const screenBounds = getSelectionScreenBounds(selectedIds, metrics);

  return (
    <TooltipProvider>
      <div
        className="shape-tool-box fade-in-0 zoom-in-95 fixed z-sticky flex -translate-x-1/2 -translate-y-full animate-in items-center gap-0.5 rounded-lg border border-neutral-200 bg-background-primary p-1 shadow-lg"
        style={
          screenBounds
            ? { left: `${screenBounds.x}px`, top: `${screenBounds.y}px` }
            : { bottom: "16px", left: "50%", transform: "translateX(-50%)" }
        }
      >
        <ShapeControlHide shape={firstShape} onUpdate={handleUpdate} />
        <ShapeControlLock shape={firstShape} onUpdate={handleUpdate} />
        {hasNonSpot && (
          <>
            <div className="h-4 w-px bg-neutral-200" />
            <ShapeControlButton
              onClick={handleFlipHorizontal}
              title="Flip Horizontal"
              icon={FlipHorizontal2}
              shortcut={shortcuts.shapeSelection.flipHorizontal}
            />
            <ShapeControlButton
              onClick={handleFlipVertical}
              title="Flip Vertical"
              icon={FlipVertical2}
              shortcut={shortcuts.shapeSelection.flipVertical}
            />
          </>
        )}
        {alignSelection ? (
          <>
            <div className="h-4 w-px bg-neutral-200" />
            <ShapeControlButton
              onClick={() => alignSelection("left")}
              title="Align left"
              icon={AlignStartVertical}
              data-align="left"
            />
            <ShapeControlButton
              onClick={() => alignSelection("centerX")}
              title="Align horizontal centres"
              icon={AlignCenterVertical}
              data-align="centerX"
            />
            <ShapeControlButton
              onClick={() => alignSelection("right")}
              title="Align right"
              icon={AlignEndVertical}
              data-align="right"
            />
            <ShapeControlButton
              onClick={() => alignSelection("top")}
              title="Align top"
              icon={AlignStartHorizontal}
              data-align="top"
            />
            <ShapeControlButton
              onClick={() => alignSelection("centerY")}
              title="Align vertical centres"
              icon={AlignCenterHorizontal}
              data-align="centerY"
            />
            <ShapeControlButton
              onClick={() => alignSelection("bottom")}
              title="Align bottom"
              icon={AlignEndHorizontal}
              data-align="bottom"
            />
          </>
        ) : null}
        {distributeSelection && selectedIds.length >= 3 ? (
          <>
            <div className="h-4 w-px bg-neutral-200" />
            <ShapeControlButton
              onClick={() => distributeSelection("x")}
              title="Distribute horizontally"
              icon={AlignHorizontalSpaceBetween}
              data-distribute="x"
            />
            <ShapeControlButton
              onClick={() => distributeSelection("y")}
              title="Distribute vertically"
              icon={AlignVerticalSpaceBetween}
              data-distribute="y"
            />
          </>
        ) : null}
        <div className="h-4 w-px bg-neutral-200" />
        <ShapeControlDelete
          shape={firstShape}
          onDelete={() => deleteShape(selectedIds)}
        />
        <div className="px-2 text-neutral-500 text-xs">
          {selectedIds.length} selected
        </div>
      </div>
    </TooltipProvider>
  );
};
